import { Field, IconButton, LabelChip, Switch } from "@carbon6/c6-frontend-design";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { PaginationFooter } from "@/components/browse";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";
import { FilterResultCount } from "@/components/filter";
import { FullscreenWindow, type WindowEntry } from "@/components/modal";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { windowStack } from "@/app/windowStack";
import { SCHEDULE_OCCURRENCE_COLUMNS } from "./scheduleOccurrenceColumns";
import {
  MAX_OCCURRENCE_LIMIT,
  ScheduleOccurrenceTableSource,
} from "./ScheduleOccurrenceTableSource";
import { scheduleStatusChipColor, scheduleStatusLabel } from "./scheduleFormat";
import type { ScheduleOccurrencesWindowParams } from "./scheduleOccurrencesWindow";
import styles from "./ScheduleOccurrencesWindowContent.module.css";

type ScheduleOccurrencesWindowContentProps = {
  entry: WindowEntry;
  // 스택에서 이 엔트리가 전체 화면으로 보이는지. false 면 마운트는 유지하고 창은 접는다.
  active: boolean;
};

/**
 * Schedule 실행 이력 전체 화면 창. 목록 행 클릭이 연다. params 는 여는 시점의 Schedule 스냅샷
 * (제목·상태 칩)이고, 이력은 source 가 API 로 받는다. source 는 여기서 소유한다. 그래서
 * 최소화해도(셸이 본문을 접는다) 받은 목록이 남는다(§12).
 */
export function ScheduleOccurrencesWindowContent({
  entry,
  active,
}: ScheduleOccurrencesWindowContentProps): ReactNode {
  const { schedule } = entry.params as ScheduleOccurrencesWindowParams;
  const [source] = useState(
    () =>
      new ScheduleOccurrenceTableSource({
        scheduleId: schedule.id,
        resolveErrorMessage: resolveLoadErrorMessage,
      }),
  );
  useEffect(() => () => source.dispose(), [source]);
  // 창(최상위)이 보관한다. 최소화로 패널이 언마운트되어도 복원하면 스위치 상태가 그대로다.
  const [includeSkipped, setIncludeSkipped] = useState(false);

  return (
    <FullscreenWindow
      open={active}
      title={entry.title}
      titleBadge={
        <LabelChip color={scheduleStatusChipColor(schedule.status)} size="sm" variant="filled">
          {scheduleStatusLabel(schedule.status)}
        </LabelChip>
      }
      headerActions={
        <IconButton
          text="새로고침"
          size="sm"
          variant="surface"
          onClick={() => void source.reload()}
        />
      }
      // 패널 루트가 height 100% 그리드로 표 스크롤을 소유한다(§14). 셸은 높이만 준다.
      bodyLayout="fill"
      onMinimize={() => windowStack.minimize(entry.id)}
      onBackdrop={() => windowStack.minimize(entry.id)}
      onRequestClose={() => windowStack.close(entry.id)}
    >
      <ScheduleOccurrencesPanel
        source={source}
        includeSkipped={includeSkipped}
        onIncludeSkippedChange={(next) => {
          setIncludeSkipped(next);
          void source.setIncludeSkipped(next);
        }}
      />
    </FullscreenWindow>
  );
}

// 표 패널. 창이 접히면 언마운트되지만 source 는 창이 보관한다. 그래서 복원하면 받은 페이지를 그대로 표시한다.
function ScheduleOccurrencesPanel({
  source,
  includeSkipped,
  onIncludeSkippedChange,
}: {
  source: ScheduleOccurrenceTableSource;
  includeSkipped: boolean;
  onIncludeSkippedChange: (next: boolean) => void;
}): ReactNode {
  useEffect(() => {
    void source.initialize();
  }, [source]);
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const loaded = snapshot.loadState === "loaded";
  // 빈 원인이 다르면 다음 행동도 다르다. 실행이 없는 경우와 필터가 모두 숨긴 경우를 구분한다.
  const emptyMessage =
    source.fetchedCount === 0
      ? "아직 실행된 적이 없습니다."
      : "모든 실행이 건너뛴 회차입니다. [건너뜀 포함] 을 켜면 볼 수 있습니다.";

  return (
    <section className={styles.panel} aria-label="Schedule 실행 이력">
      <div className={`${styles.tableArea} ${bulkBarStyles.host}`}>
        <BulkActionBar
          source={source}
          ariaLabel="실행 이력 도구"
          idleContent={
            loaded ? (
              <>
                <FilterResultCount count={snapshot.total} unitLabel="건" />
                {/* 상한을 모두 채웠다면 더 오래된 실행이 잘렸을 수 있다. 필터 때문에 보이는 수가
                    줄어도 받은 수로 판정한다. */}
                {source.fetchedCount >= MAX_OCCURRENCE_LIMIT ? (
                  <span className={styles.limitNotice}>
                    최근 {MAX_OCCURRENCE_LIMIT}건까지 보여 줍니다.
                  </span>
                ) : null}
              </>
            ) : null
          }
          // 건너뜀은 정상 회차지만 볼 일이 드물어 기본으로 숨긴다. 켜면 받은 목록을 다시 자를 뿐
          // 요청하지 않는다.
          rightContent={
            <Field label="건너뜀 포함" layout="hug" labelPlacement="end">
              <Switch
                checked={includeSkipped}
                onChange={(event) => onIncludeSkippedChange(event.target.checked)}
                aria-label="건너뜀 포함"
              />
            </Field>
          }
        />
        <HatDataTable
          source={source}
          columns={SCHEDULE_OCCURRENCE_COLUMNS}
          emptyMessage={emptyMessage}
          loadingMessage="실행 이력을 불러오고 있습니다."
          ariaLabel="실행 이력"
          scrollable
          minWidth="900px"
        />
      </div>
      <PaginationFooter
        page={snapshot.page}
        size={snapshot.size}
        total={snapshot.total}
        isLoading={snapshot.loadState === "loading"}
        ariaLabel="실행 이력 페이지 이동"
        mode="compact"
        hideWhenSinglePage
        onPrevious={() => void source.goToPage(Math.max(1, snapshot.page - 1))}
        onNext={() => void source.goToPage(snapshot.page + 1)}
      />
    </section>
  );
}
